import { ApplicationInsights, ITelemetryItem } from "@microsoft/applicationinsights-web";
import { ScrubTools } from "./ScrubTools";
import { Config } from "~/config";
import { AuthTokenProvider } from "shared-components";
import { onCLS, onFCP, onFID, onLCP, onTTFB, Metric } from "web-vitals";

export enum MetricSeverityLevel {
    Verbose = 0,
    Information = 1,
    Warning = 2,
    Error = 3,
    Critical = 4
}

let commonAppInsights: ApplicationInsights;
let classificationAppInsights: ApplicationInsights;

export const Metrics = {
    init: (): void => {
        commonAppInsights = new ApplicationInsights({
            config: {
                instrumentationKey: Config.getValue("UI:APPINSIGHTS_INSTRUMENTATION_KEY"),
                maxAjaxCallsPerView: -1
            }
        });
        loadAppInsights(commonAppInsights);

        classificationAppInsights = new ApplicationInsights({
            config: {
                instrumentationKey: Config.getValue("UI:CLASSIFICATION_APPINSIGHTS_INSTRUMENTATION_KEY"),
                maxAjaxCallsPerView: -1
            }
        });
        loadAppInsights(classificationAppInsights);
    },

    trackPageView: (pageName: string, values?: Record<string, unknown>) => {
        const { props, measures } = convertToPropsAndMeasurements(values);
        commonAppInsights.trackPageView({
            name: pageName,
            properties: props,
            measurements: measures
        });
    },

    trackTrace: (message: string, severity: MetricSeverityLevel, values?: Record<string, unknown>) => {
        const props = convertToProps(values);
        commonAppInsights.trackTrace({
            message,
            properties: props,
            severityLevel: severity.valueOf()
        });
    },

    trackEvent: (name: string, values?: Record<string, unknown>) => {
        const { props, measures } = convertToPropsAndMeasurements(values);
        commonAppInsights.trackEvent({
            name,
            properties: props,
            measurements: measures
        });
    },

    trackClassificationEvent: (name: string, values?: Record<string, unknown>) => {
        const { props, measures } = convertToPropsAndMeasurements(values);
        classificationAppInsights.trackEvent({
            name,
            properties: props,
            measurements: measures
        });
    },

    flush: async (): Promise<void> => {
        commonAppInsights.flush();

        return new Promise<void>(resolve => {
            setTimeout(() => { resolve(); }, 500);      // give some time for all network request to be sent and not cancelled by the browser
        });
    },

    initPagePerfTracker: (pageName: string) => {
        const reporter = (metric: Metric) => {
            Metrics.trackEvent("Page Performance", {
                pageName,
                metricName: metric.name,
                metricValue: metric.value
            });
        };

        // https://github.com/GoogleChrome/web-vitals/#readme
        onCLS(reporter);  // Cumulative Layout Shift
        onFID(reporter);  // First Input Delay
        onFCP(reporter);  // First Contentful Paint
        onLCP(reporter);  // Largest Contentful Paint
        onTTFB(reporter);  // Time to First Byte
    }
}

function loadAppInsights(ai: ApplicationInsights) {
    ai.loadAppInsights();

    const username = AuthTokenProvider.getUserName()

    const vstsAccount = VSS?.getWebContext()?.account.name;
    if (username) {
        ai.setAuthenticatedUserContext(ScrubTools.getHashString(username), vstsAccount, true);
    }

    ai.addTelemetryInitializer((envelope: ITelemetryItem) => {
        if (envelope.tags) {
            envelope.tags["ai.device.roleName"] = envelope.tags["ai.cloud.role"] = "product-catalog-web";
        }
        if (envelope.baseData) {
            maskPersonalInfo(envelope.baseData);
        }
    });
}

function convertToPropsAndMeasurements(values?: Record<string, unknown>) {
    let props: Record<string, string> | undefined = {};
    let measures: Record<string, number> | undefined = {};

    if (values) {
        for (const key in values) {
            const value = values[key];
            if (typeof value === "number") {
                measures[key] = value;
            } else if (value) {
                addToProps(key, value, props);
            }
        }
    }

    if (Object.keys(props).length === 0) {
        props = undefined;
    }
    if (Object.keys(measures).length === 0) {
        measures = undefined;
    }

    return { props, measures };
}

function convertToProps(values?: Record<string, unknown>) {
    const props: Record<string, string> | undefined = {};
    if (values) {
        for (const key in values) {
            const value = values[key];
            if (value) {
                addToProps(key, value, props);
            }
        }
    }

    return props;
}

function addToProps(key: string, value: unknown, props: Record<string, string>) {
    if (value) {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-base-to-string
        const strValue = `${value}`;
        props[key] = ScrubTools.maskEmail(strValue);
    }
}

function maskPersonalInfo(telemetry: Record<string, any> | undefined) {
    if (!telemetry) {
        return;
    }
    if (telemetry.url) {
        telemetry.url = ScrubTools.maskEmail(telemetry.url);
    }
    for (const key in telemetry.properties) {
        telemetry.properties[key] = ScrubTools.maskEmail(telemetry.properties[key]);
    }
}
